import { Link as RouterLink } from 'react-router-dom';
import classNames from 'classnames';
import type { ColumnDef } from '@tanstack/react-table';

import Button, { buttonVariants } from 'components/partials/button/button';
import { getStatusLabel } from 'components/partials/table-component/helpers/get-status-label';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';

import { USERS_STATUSES } from 'containers/admin/users/users.constants';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

import type { UseImpersonationResult } from 'hooks/use-impersonation';

const commonStyles = 'border-r-2 border-white font-semibold';

export interface UsersTableRow {
  id: string | number;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  lastLogin?: string | null;
  status?: string;
  viewPortalAsUser?: boolean;
  client?: { name?: string; status?: string } | null;
  [key: string]: unknown;
}

interface GetUsersTableSettingsArgs {
  clientId?: string | number | null;
  impersonation: UseImpersonationResult;
}

type UsersColumnDef = ColumnDef<UsersTableRow, unknown> & {
  headerClassName?: string;
  className?: string;
  isHeaderCentered?: boolean;
};

export const getUsersTableSettings = ({
  clientId,
  impersonation,
}: GetUsersTableSettingsArgs): UsersColumnDef[] =>
  (
    [
      {
        id: 'firstName',
        accessorKey: 'firstName',
        header: () => <span>FIRST NAME</span>,
        cell: (info) => (
          <TruncatedCell value={info.renderValue() as React.ReactNode} />
        ),
        headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md cursor-pointer`,
        className: classNames('text-left pl-4 w-[14%] max-w-0'),
        enableSorting: true,
        sortingFn: 'alphanumeric',
      },
      {
        id: 'lastName',
        accessorKey: 'lastName',
        header: () => <span>LAST NAME</span>,
        cell: (info) => (
          <TruncatedCell value={info.renderValue() as React.ReactNode} />
        ),
        headerClassName: `${commonStyles} cursor-context-menu`,
        className: classNames('text-left pl-4 max-w-0', {
          'w-[14%] sm:w-[15%]': !clientId,
          'w-[14%] md:w-[16%]': clientId,
        }),
        enableSorting: false,
      },
      !clientId && {
        id: 'client.name',
        accessorKey: 'client.name',
        header: () => <span>CLIENT NAME</span>,
        cell: (info) => info.renderValue(),
        headerClassName: `${commonStyles} cursor-pointer`,
        className: classNames('text-left pl-4', {
          'w-[16%] sm:w-[15%]': !clientId,
          'w-[18%] md:w-[17%]': clientId,
        }),
        enableSorting: true,
        sortingFn: 'alphanumeric',
      },
      {
        id: 'jobTitle',
        accessorKey: 'jobTitle',
        header: () => <span>JOB TITLE</span>,
        cell: (info) => (
          <TruncatedCell value={info.renderValue() as React.ReactNode} />
        ),
        headerClassName: `${commonStyles} cursor-context-menu`,
        className: classNames('pl-4 w-[15%] max-w-0', {
          'md:w-[13%]': !clientId,
          'md:w-[15%]': clientId,
        }),
        enableSorting: false,
      },
      {
        id: 'lastLogin',
        accessorKey: 'lastLogin',
        header: () => <span>LAST LOGIN</span>,
        cell: (info) => {
          const date = formatDate(
            info.row.original.lastLogin,
            'MMM D, YYYY h:mm A'
          );
          const label =
            info.row.original.status === 'LOCKED' &&
            getStatusLabel(info.row.original.status, USERS_STATUSES);

          return (
            <div className="flex items-center">
              <span className="pr-3">{date || 'Never'}</span>
              {label}
            </div>
          );
        },
        headerClassName: `${commonStyles} cursor-pointer`,
        className: classNames('p-4', {
          'w-[16%] sm:w-[18%] md:w-[18%]': !clientId,
          'w-[16%] md:w-[20%] ': clientId,
        }),
        enableSorting: true,
      },
      {
        id: 'profile',
        accessorKey: 'profile',
        header: () => <span>PROFILE</span>,
        cell: (info) => {
          const {
            row: {
              original: { id },
            },
          } = info;

          return (
            <div className="flex justify-center items-center">
              <RouterLink
                to={
                  clientId
                    ? `/admin/clients/${clientId}/users/${id}`
                    : `/admin/users/${id}`
                }
                data-testid="user-profile-link"
                aria-label={`View profile of ${info.row.original.firstName} ${info.row.original.lastName}`}
                className={classNames(
                  buttonVariants({ variant: 'reserve-outlined', size: 'sm' }),
                  'text-xs md:text-sm !px-2 inline-flex items-center justify-center'
                )}
              >
                <Icons.Details aria-hidden="true" />
              </RouterLink>
            </div>
          );
        },
        headerClassName: commonStyles,
        className: classNames('p-4', {
          'w-[8%] sm:w-[8%] md:w-[7%]': !clientId,
          'w-[8%] md:w-[8%] ': clientId,
        }),
        enableSorting: false,
        isHeaderCentered: true,
      },
      {
        id: 'button-cell',
        cell: (info) => {
          const {
            row: {
              original: { client, status, viewPortalAsUser },
            },
          } = info;
          const isViewBtnShown =
            !!client &&
            client.status !== 'Expired' &&
            status !== 'LOCKED' &&
            viewPortalAsUser;

          return (
            <div className="flex justify-center items-center">
              {isViewBtnShown ? (
                <Button
                  className="max-w-fit text-xs lg:text-sm"
                  onClick={() => {
                    impersonation.impersonate(
                      info.row.original as never,
                      window.location.pathname
                    );
                  }}
                  size="sm"
                  variant="brand-filled"
                  iconBefore={<Icons.Eye aria-label="EyeIcon" />}
                  data-testid="view-portal-as-user-button"
                >
                  View portal as user
                </Button>
              ) : client?.status === 'Expired' ? (
                <div className="flex justify-center">
                  <Button
                    className="max-w-fit mt-0 text-xs lg:text-sm !leading-[11px]"
                    size="sm"
                    iconBefore={<Icons.CellInfo aria-label="CellInfo" />}
                    disabled
                  >
                    Subscription is expired
                  </Button>
                </div>
              ) : status === 'LOCKED' ? (
                <div className="flex justify-center">
                  <Button
                    className="max-w-fit mt-0 text-xs lg:text-sm !leading-[11px]"
                    size="sm"
                    iconBefore={<Icons.CellInfo aria-label="CellInfo" />}
                    disabled
                  >
                    User is locked
                  </Button>
                </div>
              ) : (
                !viewPortalAsUser && (
                  <div className="flex justify-center">
                    <Button
                      className="max-w-fit mt-0 text-xs lg:text-sm !leading-[11px]"
                      size="sm"
                      iconBefore={<Icons.CellInfo aria-label="CellInfo" />}
                      disabled
                    >
                      MV internal user
                    </Button>
                  </div>
                )
              )}
            </div>
          );
        },
        headerClassName: 'h-12 rounded-tr-md rounded-br-md pl-4',
        className:
          'text-center py-3 px-4 w-[16%] md:w-[18%] lg:w-[18%] xl:w-[18%]',
      },
    ] as Array<UsersColumnDef | false | null | undefined>
  ).filter((item): item is UsersColumnDef => Boolean(item));
